<template>
  <transition
    name="slide-x-reverse-transition"
    :css="!sidebarMode && !closingSidebar"
  >
    <div
      ref="container"
      class="chat-agent"
      v-if="isVisible"
      :class="containerClasses"
      :style="containerStyle"
    >
      <div class="chat-agent__header" @click="handleHeaderClick">
        <icon-base icon="custom/ai" class="pr-2" />
        Ask CY

        <v-spacer />

        <template v-if="isSidebar">
          <!-- Sidebar mode: only offer switch-to-floating -->
          <v-btn
            icon
            small
            class="mr-1"
            @click.stop="switchToFloating"
            title="Switch to floating"
          >
            <v-icon color="light" small>mdi-window-restore</v-icon>
          </v-btn>

          <v-btn icon class="mr-1" @click.stop="close">
            <v-icon color="light">close</v-icon>
          </v-btn>
        </template>

        <template v-else>
          <!-- Floating mode: size toggle (desktop only, not when collapsed) -->
          <template v-if="!isMobile && !isCollapsed">
            <v-btn
              icon
              small
              class="mr-1"
              @click.stop="toggleSize"
              :title="isLarge ? 'Minimize' : 'Maximize'"
            >
              <v-icon color="light" small>
                {{ isLarge ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}
              </v-icon>
            </v-btn>
          </template>

          <!-- Floating mode: collapse button (desktop only) -->
          <v-btn
            v-if="!isMobile"
            icon
            small
            class="mr-1"
            @click.stop="toggleCollapse"
            :title="isCollapsed ? 'Expand' : 'Collapse'"
          >
            <v-icon color="light" small>
              {{ isCollapsed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>

          <!-- Floating mode: dock to sidebar (desktop only) -->
          <v-btn
            v-if="!isMobile"
            icon
            small
            class="mr-1"
            :disabled="!allowSidebar"
            @click.stop="switchToSidebar"
            title="Dock to sidebar"
          >
            <v-icon color="light" small>mdi-dock-right</v-icon>
          </v-btn>

          <v-btn icon class="mr-1" @click.stop="close">
            <v-icon color="light">close</v-icon>
          </v-btn>
        </template>
      </div>

      <v-expand-transition>
        <div v-show="!isCollapsed" class="chat-agent__content">
          <div class="chat-agent__body" ref="messagesContainer">
            <!--
              The server would not let us connect and we have stopped retrying.
              Without this the input stayed enabled, messages queued into a
              buffer that was never flushed, and the spinner ran forever.
            -->
            <page-placeholder
              v-if="connectionRefused"
              :config="refusedPlaceholderConfig"
            />

            <div
              v-for="(message, index) in messages"
              :key="index"
              class="chat-agent__message"
              :class="messageClasses(message)"
            >
              <!-- Error display -->
              <div v-if="message.error" class="chat-agent__error">
                <v-icon small color="error" class="mr-2"
                  >mdi-alert-circle</v-icon
                >
                <span>{{ friendlyErrorMessage(message.error) }}</span>
              </div>

              <!-- Tool calls display -->
              <div v-if="hasToolCalls(message)" class="chat-agent__tools">
                <div
                  class="chat-agent__tools-header"
                  @click="toggleToolDetails(message.id)"
                >
                  <v-icon small class="mr-2" color="appMain">
                    {{
                      isToolDetailsOpen(message.id)
                        ? 'mdi-chevron-down'
                        : 'mdi-chevron-right'
                    }}
                  </v-icon>
                  <v-icon
                    small
                    class="mr-2"
                    :class="{
                      'chat-agent__tools-spinner': hasActiveTools(message),
                    }"
                  >
                    {{
                      hasActiveTools(message)
                        ? 'mdi-loading'
                        : 'mdi-check-circle'
                    }}
                  </v-icon>
                  <span>{{ toolStatusText(message) }}</span>
                </div>

                <v-expand-transition>
                  <div
                    v-if="isToolDetailsOpen(message.id)"
                    class="chat-agent__tools-details"
                  >
                    <div
                      v-for="tool in message.toolCalls"
                      :key="tool.id"
                      class="chat-agent__tools-item"
                    >
                      <div class="chat-agent__tools-item-header">
                        <v-icon x-small class="mr-1">
                          {{
                            tool.status === 'pending'
                              ? 'mdi-clock-outline'
                              : 'mdi-check'
                          }}
                        </v-icon>
                        {{ friendlyToolName(tool.name) }}
                      </div>
                      <ul
                        v-if="hasToolArgs(tool)"
                        class="chat-agent__tools-args"
                      >
                        <template
                          v-for="(value, argKey) in getDisplayableArgs(tool)"
                        >
                          <li :key="argKey" class="chat-agent__tools-arg">
                            <span class="chat-agent__tools-arg-key"
                              >{{ formatArgKey(argKey) }}:</span
                            >
                            <span
                              v-if="!isNestedObject(value)"
                              class="chat-agent__tools-arg-value"
                            >
                              {{ formatArgValue(value) }}
                            </span>
                            <span
                              v-else
                              class="chat-agent__tools-arg-value chat-agent__tools-arg-value--nested"
                            >
                              {<span class="chat-agent__tools-nested-count"
                                >{{ Object.keys(value).length }} items</span
                              >}
                            </span>
                          </li>
                          <!-- Nested object children -->
                          <li
                            v-for="(
                              nestedValue, nestedKey
                            ) in getNestedDisplayableArgs(value)"
                            :key="`${argKey}.${nestedKey}`"
                            class="chat-agent__tools-arg chat-agent__tools-arg--nested"
                          >
                            <span class="chat-agent__tools-arg-key"
                              >{{ formatArgKey(nestedKey) }}:</span
                            >
                            <span class="chat-agent__tools-arg-value">{{
                              formatArgValue(nestedValue)
                            }}</span>
                          </li>
                        </template>
                      </ul>
                    </div>
                  </div>
                </v-expand-transition>
              </div>

              <!-- Message content -->
              <div v-if="message.content" class="chat-agent__message-content">
                <!-- Bot/assistant messages use StreamingMarkdown for rich rendering -->
                <streaming-markdown
                  v-if="isBotMessage(message)"
                  :content="message.content"
                  :streaming="message.status === 'streaming'"
                />
                <!-- User messages render as plain text -->
                <template v-else>{{ message.content }}</template>
              </div>

              <!-- Per-message feedback (thumbs up/down) -->
              <div
                v-if="isBotMessage(message) && message.status === 'complete'"
                class="chat-agent__message-feedback"
              >
                <v-btn
                  icon
                  x-small
                  :color="
                    message.feedback === 'positive'
                      ? 'success'
                      : 'grey lighten-1'
                  "
                  @click="submitMessageFeedback(message, 'positive')"
                  title="Helpful"
                >
                  <v-icon small>mdi-thumb-up-outline</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  :color="
                    message.feedback === 'negative' ? 'error' : 'grey lighten-1'
                  "
                  @click="submitMessageFeedback(message, 'negative')"
                  title="Not helpful"
                >
                  <v-icon small>mdi-thumb-down-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>

          <div class="chat-agent__form">
            <v-progress-linear
              v-if="isLoading"
              height="3"
              indeterminate
              color="appMain"
              class="chat-agent__progress"
            />

            <div v-if="stagedPaste" class="chat-agent__staged">
              <div class="chat-agent__staged-pill">
                <v-icon x-small class="mr-1" color="grey darken-1"
                  >mdi-content-paste</v-icon
                >
                <span class="chat-agent__staged-label">Pasted text</span>
                <span class="chat-agent__staged-preview">{{
                  stagedPastePreview
                }}</span>
                <v-btn
                  icon
                  x-small
                  @click="removeStagedPaste"
                  class="chat-agent__staged-remove"
                >
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <v-textarea
              ref="messageInput"
              :placeholder="
                stagedPaste ? 'Type your prompt...' : 'Type your message...'
              "
              class="no-label"
              hide-details
              rows="4"
              auto-grow
              v-model="userQuery"
              :disabled="isLoading || connectionRefused"
              @keydown.enter.exact="handleEnterKey"
              @paste.native="handlePaste"
            />

            <v-btn
              icon
              color="appMain"
              class="chat-agent__form-send"
              :disabled="
                (!userQuery && !stagedPaste) || isLoading || connectionRefused
              "
              @click="sendMessage"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
        </div>
      </v-expand-transition>

      <!-- Collapsed status indicator -->
      <div
        v-if="isCollapsed && hasActivity"
        class="chat-agent__collapsed-indicator"
      >
        <v-icon small color="white" class="mr-1">mdi-loading mdi-spin</v-icon>
        <span>Working...</span>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import EventBus from '@/factories/eventBus';
import lackey from '@/dev/lackey';
import StreamingMarkdown from '@/components/basic/streaming-markdown/StreamingMarkdown.vue';
import PagePlaceholder from '@/components/basic/placeholders/PagePlaceholder.vue';
import { buildAppNavTree } from '@/lib/buildAppNavTree';
import { CHAT_AGENT_EVENTS } from '@/lib/chatAgentKit';
import { FEATURES } from '@/access/features';
import { chatSurface } from '@/components/pages/pagesPreview';
import { appRoute } from '@/globals';

// Agent type for chat agent
const AGENT_TYPE = 'chat';

const MAX_NAV_HISTORY = 10;

// Display modes
const DISPLAY_MODE = {
  LARGE: 'large',
  SMALL: 'small',
  COLLAPSED: 'collapsed',
};

// Layout constants
const LAYOUT = {
  MARGIN: 20,
  HEADER_HEIGHT: 52, // Actual computed header height for collapsed mode
  LARGE_TOP: 65,
  LARGE_WIDTH: 520,
  SMALL_WIDTH: 440,
  SMALL_HEIGHT: 580,
  COLLAPSED_WIDTH: 320,
  SIDEBAR_WIDTH: 400,
};

// Animation configuration
const ANIMATION_DURATION = 350;
const ANIMATION_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'; // Expo ease-out

const PASTE_STAGE_MIN_LINES = 4;
const PASTE_STAGE_MIN_CHARS = 300;

/**
 * Prefix each node path with the SPA base (e.g. /ui) so markdown links match the real URL.
 * @param {Array|undefined} nodes
 * @returns {Array|undefined}
 */
function applyAppBaseToNavTree(nodes) {
  if (!nodes?.length) {
    return nodes;
  }
  return nodes.map((n) => ({
    ...n,
    path: n.path != null ? appRoute(n.path) : undefined,
    children: n.children?.length
      ? applyAppBaseToNavTree(n.children)
      : undefined,
  }));
}

export default {
  name: 'ChatAgent',
  components: {
    StreamingMarkdown,
    PagePlaceholder,
  },
  props: {
    // Unique instance name for this chat session (UUID for new conversations)
    instanceNameProp: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isVisible: false,
      userQuery: '',
      stagedPaste: null,

      // Display mode: 'large', 'small' (default), or 'collapsed'
      displayMode: DISPLAY_MODE.SMALL,

      // Track previous mode before collapsing
      previousMode: DISPLAY_MODE.SMALL,

      // User's preferred size (persists across open/close, excludes collapsed)
      preferredMode: DISPLAY_MODE.SMALL,

      // Animation state
      isAnimating: false,
      currentDimensions: null,

      // Instance name - use prop if provided, will generate UUID when opening if empty
      instanceName: '',

      // Track which messages have expanded tool details
      expandedToolDetails: {},

      // Track if this is the first message (for setting displayName)
      hasSetDisplayName: false,

      // Explicit page-level context set by components (e.g. LessonEdit, Lessons).
      // Replaced (not merged) on every updateChatAgentContext / open({ context }) call.
      pageContext: null,

      // Rolling buffer of the last MAX_NAV_HISTORY route changes (auto-managed).
      navigationHistory: [],

      // Sidebar mode: when true, renders as in-flow panel instead of fixed overlay
      sidebarMode: false,
      allowSidebar: true,

      // True while the sidebar leave transition is in progress — keeps isSidebar true
      // so position:static is maintained until the element is actually removed from DOM
      closingSidebar: false,

      // Reactive viewport (window.inner* is not a Vue dependency — use this for layout)
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      resizeRafId: null,
    };
  },
  computed: {
    ...mapGetters('agents', ['agent', 'isConnected', 'agentsHost']),
    ...mapGetters('users', ['currentUser', 'isAdmin']),
    ...mapState('customObjects', ['standaloneObjects']),

    agentData() {
      return this.agent(AGENT_TYPE, this.instanceName);
    },

    messages() {
      return this.agentData.messages;
    },

    isLoading() {
      return this.agentData.loading;
    },

    connected() {
      return this.agentData.connected;
    },

    /**
     * The store gave up after a bounded number of attempts that never opened.
     * Distinct from `disconnected`, which is still retrying in the background.
     */
    connectionRefused() {
      return this.agentData.refused;
    },

    refusedPlaceholderConfig() {
      return {
        title: 'Assistant unavailable',
        // Deliberately not "you are not authorized" — an admin hitting this has
        // almost certainly done nothing wrong. The common causes are an expired
        // session and the assistant service being unreachable; the one genuinely
        // unauthorized case is not worth accusing everyone else over.
        text: 'We could not connect to the assistant. This usually clears up on its own — try again, or reload the page if it keeps happening.',
        buttonLabel: 'Try again',
        buttonPrefix: '',
        create: () => this.retryConnection(),
      };
    },

    isLarge() {
      return this.displayMode === DISPLAY_MODE.LARGE;
    },

    isSmall() {
      return this.displayMode === DISPLAY_MODE.SMALL;
    },

    isCollapsed() {
      return this.displayMode === DISPLAY_MODE.COLLAPSED;
    },

    hasActivity() {
      return (
        this.isLoading || this.messages.some((m) => m.status === 'streaming')
      );
    },

    stagedPastePreview() {
      if (!this.stagedPaste) return '';
      const lines = this.stagedPaste.split('\n').filter((l) => l.trim());
      const preview = lines.slice(0, 2).join(' ');
      if (preview.length > 80) return preview.substring(0, 77) + '...';
      if (lines.length > 2) return preview + '...';
      return preview;
    },

    isSidebar() {
      // closingSidebar keeps this true during exit so position:static persists
      // until the element is removed — without it, isSidebar drops to false the
      // moment isVisible is false, switching to position:fixed mid-transition and
      // causing the panel to jump/slide from the wrong location.
      return (this.sidebarMode || this.closingSidebar) && !this.isMobile;
    },

    containerClasses() {
      return {
        'chat-agent--mobile': this.isMobile,
        'chat-agent--large': this.isLarge && !this.isMobile,
        'chat-agent--small': this.isSmall && !this.isMobile,
        'chat-agent--collapsed': this.isCollapsed && !this.isMobile,
        'chat-agent--animating': this.isAnimating,
        'chat-agent--sidebar': this.isSidebar,
      };
    },

    // Calculate target dimensions based on current mode and viewport
    targetDimensions() {
      const vh = this.viewportHeight;
      const vw = this.viewportWidth;

      if (this.isMobile) {
        return {
          top: 0,
          right: 0,
          width: vw,
          height: vh,
        };
      }

      if (this.isCollapsed) {
        return {
          top: vh - LAYOUT.HEADER_HEIGHT - LAYOUT.MARGIN,
          right: LAYOUT.MARGIN,
          width: LAYOUT.COLLAPSED_WIDTH,
          height: LAYOUT.HEADER_HEIGHT,
        };
      }

      if (this.isLarge) {
        return {
          top: LAYOUT.LARGE_TOP,
          right: LAYOUT.MARGIN,
          width: LAYOUT.LARGE_WIDTH,
          height: vh - LAYOUT.LARGE_TOP - LAYOUT.MARGIN,
        };
      }

      // Small mode
      return {
        top: vh - LAYOUT.SMALL_HEIGHT - LAYOUT.MARGIN,
        right: LAYOUT.MARGIN,
        width: LAYOUT.SMALL_WIDTH,
        height: LAYOUT.SMALL_HEIGHT,
      };
    },

    // Inline styles for positioning (used during and after animation)
    containerStyle() {
      if (this.isMobile) return {};

      const dims = this.currentDimensions || this.targetDimensions;

      // Sidebar mode: only width matters; position handled by flow layout
      if (this.isSidebar) {
        return {
          width: `${LAYOUT.SIDEBAR_WIDTH}px`,
        };
      }

      // During FLIP animation we drive explicit top/width/height
      if (this.currentDimensions) {
        return {
          position: 'fixed',
          top: `${dims.top}px`,
          right: `${dims.right}px`,
          width: `${dims.width}px`,
          height: `${dims.height}px`,
          bottom: 'auto',
          margin: 0,
        };
      }

      // Small / collapsed: anchor to bottom-right so the panel stays in view when vh shrinks
      if (this.isSmall || this.isCollapsed) {
        return {
          position: 'fixed',
          bottom: `${LAYOUT.MARGIN}px`,
          right: `${LAYOUT.MARGIN}px`,
          width: `${dims.width}px`,
          height: `${dims.height}px`,
          top: 'auto',
          margin: 0,
        };
      }

      // Large: fixed top + bottom so height tracks viewport without stale pixel height
      if (this.isLarge) {
        return {
          position: 'fixed',
          top: `${LAYOUT.LARGE_TOP}px`,
          bottom: `${LAYOUT.MARGIN}px`,
          right: `${LAYOUT.MARGIN}px`,
          width: `${LAYOUT.LARGE_WIDTH}px`,
          height: 'auto',
          margin: 0,
        };
      }

      return {
        position: 'fixed',
        top: `${dims.top}px`,
        right: `${dims.right}px`,
        width: `${dims.width}px`,
        height: `${dims.height}px`,
        bottom: 'auto',
        margin: 0,
      };
    },

    /** Auth snapshot for buildAppNavTree (mirrors AppSidebar / SecondaryNav / Settings filters) */
    chatNavAuthContext() {
      const standalone = this.standaloneObjects || [];
      return {
        hasPermission: this.hasPermission,
        isFeatureEnabled: this.isFeatureEnabled,
        isVectorRecruiting: this.isVectorRecruiting,
        userRole: this.currentUser?.role ?? null,
        hasStandaloneObjects: standalone.length > 0,
        isAdmin: this.isAdmin,
        currentUser: this.currentUser,
      };
    },
  },
  watch: {
    isSidebar(active) {
      this.applySidebarLayoutClass(active);
      EventBus.$emit(CHAT_AGENT_EVENTS.SIDEBAR_STATE, active);
    },

    isVisible(isVisible) {
      if (!isVisible) return;

      if (!this.isMobile) {
        this.focusTextarea();
      }

      setTimeout(() => {
        this.scrollToBottom(true);
      }, 100);
    },

    messages: {
      handler() {
        this.scrollToBottom();
      },
      deep: true,
    },

    '$route.hash': {
      handler(newHash) {
        if (newHash === '#cy' && !this.isVisible) {
          this.open();
        }
      },
      immediate: true,
    },

    $route(to) {
      const path = to.path;
      const last = this.navigationHistory[this.navigationHistory.length - 1];
      if (last && last.path === path) return;

      this.navigationHistory.push({
        path,
        fullPath: to.fullPath,
        name: to.name || null,
        timestamp: Date.now(),
      });

      if (this.navigationHistory.length > MAX_NAV_HISTORY) {
        this.navigationHistory.splice(
          0,
          this.navigationHistory.length - MAX_NAV_HISTORY
        );
      }
    },
  },
  methods: {
    /**
     * Generate a UUID v4
     */
    generateUUID() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      // Fallback UUID v4 generation
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },

    /**
     * Ensure we have a valid instance name (UUID), generating one if needed
     */
    ensureInstanceName() {
      if (!this.instanceName) {
        this.instanceName = this.generateUUID();
      }
      return this.instanceName;
    },

    /**
     * Connect to the Cloudflare agent.
     * Always persists to the backend; the connect action deduplicates by UUID.
     */
    connectToAgent() {
      this.ensureInstanceName();

      this.$store.dispatch('agents/connect', {
        agent: AGENT_TYPE,
        name: this.instanceName,
      });

      // Register this instance in the registry
      const key = `${AGENT_TYPE}:${this.instanceName}`;
      this.$store.commit('agents/registerInstance', {
        key,
        agentType: AGENT_TYPE,
        instanceName: this.instanceName,
      });
    },

    /**
     * User asked to try again after we gave up. The connect action allows a
     * 'refused' key through and resets the attempt budget, so this is just a
     * plain reconnect.
     */
    retryConnection() {
      this.connectToAgent();
    },

    async focusTextarea() {
      if (this.isCollapsed) return;
      await this.$nextTick();
      this.$refs.messageInput?.$el.querySelector('textarea')?.focus();
    },

    /**
     * Build the backchannel context payload sent with each message.
     * Combines separated pageContext + navigationHistory + nav tree + user info.
     */
    getAgentContextPayload() {
      const { tree, visibleNavLabels } = buildAppNavTree(
        this.chatNavAuthContext
      );
      const treeWithBase = applyAppBaseToNavTree(tree);

      const currentRoute = {
        fullPath: this.$route.fullPath,
        name: this.$route.name || null,
      };

      const pc = this.pageContext ? { ...this.pageContext } : null;
      if (pc) {
        pc.stale = pc.setRoute?.fullPath !== this.$route.fullPath;
      }

      const surface = chatSurface(this.$route, this.isFeatureEnabled);

      return {
        pageContext: pc,
        navigationHistory: [...this.navigationHistory],
        user: {
          role: this.currentUser?.role ?? null,
          permissions: this.currentUser?.permissions ?? [],
          features: {
            pages: this.isFeatureEnabled(FEATURES.PAGES),
          },
          ...(surface ? { surface } : {}),
        },
        navigation: {
          tree: treeWithBase,
          visibleNavItems: visibleNavLabels,
          currentRoute,
        },
        timestamp: Date.now(),
      };
    },

    handleEnterKey(event) {
      event.preventDefault();
      this.sendMessage();
    },

    sendMessage() {
      const typed = this.userQuery?.trim();
      const pasted = this.stagedPaste || '';
      let content = '';

      if (typed && pasted) {
        content = `${typed}\n\n${pasted}`;
      } else {
        content = typed || pasted;
      }

      if (!content) return;

      // Belt and braces: the input and send button are already disabled when
      // we have given up connecting, but sendMessage is also reachable from the
      // Enter key handler and from the retry-after-connect path. Sending here
      // would push the message into a buffer that is never flushed.
      if (this.connectionRefused) return;

      const contextPayload = this.getAgentContextPayload();
      // eslint-disable-next-line no-console -- dev visibility for backchannel context
      console.log(
        '[ChatAgent] cf_agent_context_update payload',
        contextPayload
      );

      if (contextPayload && Object.keys(contextPayload).length > 0) {
        this.$store.dispatch('agents/updateContext', {
          agent: AGENT_TYPE,
          name: this.instanceName,
          context: contextPayload,
        });
      }

      // Set display name from the first message (first 50 chars)
      // This updates both the local registry and syncs to the backend
      if (!this.hasSetDisplayName) {
        const displayName = (typed || content).substring(0, 50);
        this.$store.dispatch('agents/updateAgentDisplayName', {
          agent: AGENT_TYPE,
          name: this.instanceName,
          displayName,
        });
        this.hasSetDisplayName = true;
      }

      this.$store.dispatch('agents/sendMessage', {
        agent: AGENT_TYPE,
        name: this.instanceName,
        content,
      });

      this.userQuery = '';
      this.stagedPaste = null;
      this.scrollToBottom(true);
    },

    submitMessageFeedback(message, rating) {
      this.$store.dispatch('agents/submitFeedback', {
        agent: AGENT_TYPE,
        name: this.instanceName,
        messageId: message.id,
        turnId: message.turnId || message.id,
        rating,
      });
    },

    isBotMessage(message) {
      // Handle both 'assistant' (CF Agents) and 'bot' (legacy) roles
      return message.role === 'assistant' || message.role === 'bot';
    },

    messageRole(message) {
      // Normalize role for CSS classes
      if (message.role === 'assistant') return 'bot';
      return message.role;
    },

    messageClasses(message) {
      const classes = [`chat-agent__message--${this.messageRole(message)}`];
      if (message.error) {
        classes.push('chat-agent__message--error');
      }
      if (message.status === 'streaming') {
        classes.push('chat-agent__message--streaming');
      }
      return classes;
    },

    // Tool call helpers
    hasToolCalls(message) {
      return message.toolCalls && message.toolCalls.length > 0;
    },

    hasActiveTools(message) {
      return message.toolCalls?.some((tc) => tc.status === 'pending');
    },

    toggleToolDetails(messageId) {
      this.expandedToolDetails = {
        ...this.expandedToolDetails,
        [messageId]: !this.expandedToolDetails[messageId],
      };
    },

    isToolDetailsOpen(messageId) {
      return !!this.expandedToolDetails[messageId];
    },

    toolStatusText(message) {
      const tools = message.toolCalls || [];
      const pending = tools.filter((tc) => tc.status === 'pending');

      if (pending.length > 0) {
        if (pending.length === 1) {
          return `${this.friendlyToolName(pending[0].name)}...`;
        }
        return `Running ${pending.length} tools...`;
      }

      if (tools.length === 1) {
        return `Used ${this.friendlyToolName(tools[0].name)}`;
      }
      return `Used ${tools.length} tools`;
    },

    friendlyToolName(toolName) {
      const toolLabels = {
        searchHelpDocs: 'Search help articles',
        getToolGuide: 'Load tool guide',
        getLessonItemSchema: 'Look up item schema',
        editLesson: 'Edit lesson',
        getUser: 'Get user info',
        getVideoTranscript: 'Fetch video transcript',
        getRecord: 'Fetch record',
        getRecords: 'Search records',
        scheduleTask: 'Schedule task',
        getScheduledTasks: 'Get scheduled tasks',
        cancelScheduledTask: 'Cancel scheduled task',
        buildPage: 'Build page',
      };
      return toolLabels[toolName] || this.humanizeToolName(toolName);
    },

    humanizeToolName(toolName) {
      // Convert camelCase or snake_case to readable text
      return toolName
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase());
    },

    hasToolArgs(tool) {
      return (
        tool.args &&
        typeof tool.args === 'object' &&
        Object.keys(tool.args).length > 0
      );
    },

    getDisplayableArgs(tool) {
      if (!tool.args || typeof tool.args !== 'object') return {};
      // Filter out empty values and internal/technical fields if desired
      return Object.fromEntries(
        Object.entries(tool.args).filter(([, value]) => {
          // Skip null, undefined, or empty strings
          if (value === null || value === undefined || value === '')
            return false;
          // Skip empty arrays
          if (Array.isArray(value) && value.length === 0) return false;
          // Skip empty objects
          if (
            typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.keys(value).length === 0
          )
            return false;
          return true;
        })
      );
    },

    formatArgKey(key) {
      // Convert snake_case or camelCase to readable label
      return key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase();
    },

    formatArgValue(value) {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'boolean') return value ? 'yes' : 'no';
      if (Array.isArray(value)) {
        if (value.length === 0) return '-';
        if (value.length <= 3) return value.join(', ');
        return `${value.slice(0, 3).join(', ')} (+${value.length - 3} more)`;
      }
      if (typeof value === 'object') {
        const keys = Object.keys(value);
        if (keys.length === 0) return '-';
        return `{${keys.length} items}`;
      }
      // Truncate long strings
      const str = String(value);
      if (str.length > 60) {
        return str.substring(0, 57) + '...';
      }
      return str;
    },

    isNestedObject(value) {
      return (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).length > 0
      );
    },

    getNestedDisplayableArgs(value) {
      if (!this.isNestedObject(value)) return {};
      // Filter out empty values, same logic as getDisplayableArgs
      return Object.fromEntries(
        Object.entries(value).filter(([, v]) => {
          if (v === null || v === undefined || v === '') return false;
          if (Array.isArray(v) && v.length === 0) return false;
          if (
            typeof v === 'object' &&
            !Array.isArray(v) &&
            Object.keys(v).length === 0
          )
            return false;
          return true;
        })
      );
    },

    // Error helpers
    friendlyErrorMessage(error) {
      // Map technical errors to user-friendly messages
      if (typeof error === 'string') {
        // Check for common error patterns
        if (error.includes('rate limit') || error.includes('too many')) {
          return 'Too many requests. Please wait a moment and try again.';
        }
        if (error.includes('timeout') || error.includes('timed out')) {
          return 'The request took too long. Please try again.';
        }
        if (error.includes('network') || error.includes('connection')) {
          return 'Connection issue. Please check your internet and try again.';
        }
        // Return a cleaned up version of short errors
        if (error.length < 100) {
          return error;
        }
      }
      // Default fallback
      return 'Something went wrong. Please try again.';
    },

    handlePaste(event) {
      const text = event.clipboardData?.getData('text');
      if (!text) return;

      if (this.stagedPaste) return;

      const lineCount = text.split('\n').length;
      if (
        lineCount < PASTE_STAGE_MIN_LINES &&
        text.length < PASTE_STAGE_MIN_CHARS
      ) {
        return;
      }

      event.preventDefault();
      this.stagedPaste = text;
    },

    removeStagedPaste() {
      this.stagedPaste = null;
    },

    async scrollToBottom(force = false) {
      await this.$nextTick();
      const tolerance = 60;
      const messagesContainer = this.$refs.messagesContainer;

      if (!messagesContainer) return;

      const isScrollAtBottom =
        messagesContainer.scrollTop + messagesContainer.clientHeight >=
        messagesContainer.scrollHeight - tolerance;

      if (!isScrollAtBottom && !force) return;

      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    },

    // FLIP Animation system for smooth transitions
    async animateToMode(newMode) {
      if (this.isMobile || !this.$refs.container) {
        this.displayMode = newMode;
        return;
      }

      // FIRST: Capture current position
      const first = this.targetDimensions;

      // Store current dimensions to freeze the element
      this.currentDimensions = { ...first };

      // LAST: Calculate new position by temporarily setting the mode
      const oldMode = this.displayMode;
      this.displayMode = newMode;
      await this.$nextTick();
      const last = this.targetDimensions;

      // If no change, just update and return
      if (
        first.top === last.top &&
        first.width === last.width &&
        first.height === last.height
      ) {
        this.currentDimensions = null;
        return;
      }

      // INVERT & PLAY: Animate from first to last
      this.isAnimating = true;

      // Use Web Animations API for GPU-accelerated smoothness
      const animation = this.$refs.container.animate(
        [
          {
            top: `${first.top}px`,
            width: `${first.width}px`,
            height: `${first.height}px`,
          },
          {
            top: `${last.top}px`,
            width: `${last.width}px`,
            height: `${last.height}px`,
          },
        ],
        {
          duration: ANIMATION_DURATION,
          easing: ANIMATION_EASING,
          fill: 'forwards',
        }
      );

      animation.onfinish = () => {
        // fill:'forwards' keeps the WAAPI effect after finish, which overrides :style for
        // top/width/height and breaks viewport + bottom-anchored updates until removed.
        this.currentDimensions = null;
        this.isAnimating = false;

        this.$nextTick(() => {
          animation.cancel();

          if (!this.isCollapsed) {
            this.focusTextarea();
            this.scrollToBottom(true);
          }
        });
      };
    },

    // Display mode controls
    switchToFloating() {
      this.sidebarMode = false;
    },

    switchToSidebar() {
      if (!this.allowSidebar) return;
      this.sidebarMode = true;
    },

    toggleSize() {
      const newMode = this.isLarge ? DISPLAY_MODE.SMALL : DISPLAY_MODE.LARGE;
      this.preferredMode = newMode; // Remember user's size preference
      this.animateToMode(newMode);
    },

    toggleCollapse() {
      if (this.isCollapsed) {
        // Restore to preferred mode
        this.animateToMode(this.preferredMode);
      } else {
        // Save current mode and collapse
        this.previousMode = this.displayMode;
        this.animateToMode(DISPLAY_MODE.COLLAPSED);
      }
    },

    syncViewportSize() {
      if (typeof window === 'undefined') return;
      this.viewportHeight = window.innerHeight;
      this.viewportWidth = window.innerWidth;
    },

    /**
     * Batch viewport reads on resize so targetDimensions (and bottom-anchored layout) stay correct.
     */
    handleViewportResize() {
      if (this.resizeRafId) {
        cancelAnimationFrame(this.resizeRafId);
      }
      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null;
        this.syncViewportSize();
        if (!this.isMobile && !this.isAnimating) {
          this.currentDimensions = null;
        }
      });
    },

    handleHeaderClick() {
      // Allow clicking header to expand when collapsed
      if (this.isCollapsed) {
        this.toggleCollapse();
      }
    },

    async open(options) {
      const wasCollapsed = this.isVisible && this.isCollapsed;

      // Support both string (legacy) and object formats
      if (typeof options === 'string') {
        options = { search: options };
      }

      // Set sidebar mode BEFORE isVisible — the :css binding on <transition> reads
      // sidebarMode to decide whether to skip CSS transitions (instant for sidebar,
      // slide for floating). Both must be settled before Vue renders the enter.
      this.allowSidebar = options?.allowSidebar !== false;
      this.sidebarMode = this.allowSidebar && !!options?.sidebar;
      this.closingSidebar = false;
      this.isVisible = true;

      // If was collapsed, animate to preferred size; otherwise just set it
      if (wasCollapsed) {
        this.animateToMode(this.preferredMode);
      } else if (this.displayMode !== this.preferredMode) {
        this.displayMode = this.preferredMode;
      }

      // Store backchannel context as pageContext (same as updateContext)
      if (options?.context) {
        this.updateContext(options.context);
      }

      // Determine the instance name to use
      const requestedInstanceName = options?.instanceName || '';
      const needsNewInstance =
        requestedInstanceName && requestedInstanceName !== this.instanceName;
      const needsInitialConnection = !this.instanceName && !this.connected;

      // Switch to a different instance if specified, or create a new one if this is the first open
      const needsConnectionWait = options?.search || options?.prefill;

      if (needsNewInstance) {
        this.instanceName = requestedInstanceName;
        this.hasSetDisplayName = false;
        this.connectToAgent();

        if (needsConnectionWait) {
          await this.waitForConnection();
        }
      } else if (needsInitialConnection) {
        this.ensureInstanceName();
        this.hasSetDisplayName = false;
        this.connectToAgent();

        if (needsConnectionWait) {
          await this.waitForConnection();
        }
      }

      if (options?.displayName) {
        this.$store.dispatch('agents/updateAgentDisplayName', {
          agent: AGENT_TYPE,
          name: this.instanceName,
          displayName: options.displayName,
        });
        this.hasSetDisplayName = true;
      }

      if (options?.search) {
        this.userQuery = options.search;
        this.sendMessage();

        setTimeout(() => {
          this.scrollToBottom(true);
        }, 300);
      } else if (options?.prefill) {
        await this.applyPrefillIfEmpty(options.prefill);
      }
    },

    /**
     * Pre-fill the input only when this instance has no conversation history.
     * Waits briefly after connect for the server to sync existing messages.
     */
    async applyPrefillIfEmpty(prefillText) {
      if (!prefillText || this.userQuery?.trim()) {
        return;
      }

      const key = `${AGENT_TYPE}:${this.instanceName}`;
      let messages = this.$store.state.agents.messages[key] || [];

      if (messages.length === 0 && this.connected) {
        await this.waitForMessagesLoaded(key);
        messages = this.$store.state.agents.messages[key] || [];
      }

      if (messages.length === 0) {
        this.userQuery = prefillText;
        this.focusTextarea();
      }
    },

    /**
     * Wait for the server to send existing messages after connect.
     * Resolves immediately if messages are already present, or after a short
     * timeout when the server has none to send.
     */
    waitForMessagesLoaded(key, timeoutMs = 500) {
      return new Promise((resolve) => {
        const existing = this.$store.state.agents.messages[key] || [];
        if (existing.length > 0) {
          resolve();
          return;
        }

        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          unsubscribe();
          clearTimeout(timer);
          resolve();
        };

        const unsubscribe = this.$store.subscribe((mutation) => {
          if (
            mutation.type === 'agents/setMessages' &&
            mutation.payload?.key === key &&
            (mutation.payload.messages || []).length > 0
          ) {
            finish();
          }
        });

        const timer = setTimeout(finish, timeoutMs);
      });
    },

    /**
     * Wait for the agent connection to be established
     * Returns a promise that resolves when connected or times out after 5 seconds
     */
    waitForConnection() {
      return new Promise((resolve) => {
        const maxAttempts = 50; // 5 seconds total (50 * 100ms)
        let attempts = 0;

        const checkConnection = () => {
          attempts++;
          if (this.connected || attempts >= maxAttempts) {
            resolve();
            return;
          }
          setTimeout(checkConnection, 100);
        };

        // Start checking
        checkConnection();
      });
    },

    /**
     * Replace page-level context. Auto-stamps the current route so
     * staleness can be detected at send time.
     */
    updateContext(context) {
      this.pageContext = {
        ...context,
        setRoute: {
          fullPath: this.$route.fullPath,
          name: this.$route.name || null,
        },
      };
    },

    resetInstance() {
      this.instanceName = '';
      this.pageContext = null;
      this.navigationHistory = [];
      this.hasSetDisplayName = false;
    },

    close() {
      if (this.sidebarMode) {
        // Keep isSidebar=true during the (instant, css=false) leave so position:static
        // is not dropped before the element is removed from DOM.
        this.closingSidebar = true;
        // Trigger layout reflow immediately so the main content expands at the same
        // time the panel disappears, not one tick later via the isSidebar watcher.
        this.applySidebarLayoutClass(false);
      }
      this.isVisible = false;
      this.sidebarMode = false;
      if (this.closingSidebar) {
        this.$nextTick(() => {
          this.closingSidebar = false;
        });
      }
    },

    applySidebarLayoutClass(active) {
      const wrap = document.querySelector('.v-application--wrap');
      if (!wrap) return;
      wrap.classList.toggle('cy-sidebar-active', active);

      this.$nextTick(() => {
        const header = document.querySelector('.app-header');
        if (!header) return;
        if (active) {
          header.style.setProperty('position', 'absolute', 'important');
          header.style.setProperty('left', '0px', 'important');
          header.style.setProperty('right', '0px', 'important');
          header.style.setProperty('width', 'auto', 'important');
        } else {
          header.style.removeProperty('position');
          header.style.removeProperty('left');
          header.style.removeProperty('right');
          header.style.removeProperty('width');
        }
      });
    },

    clearHistory() {
      this.$store.dispatch('agents/clearHistory', {
        agent: AGENT_TYPE,
        name: this.instanceName,
      });
    },
  },
  created() {
    // Use prop if provided
    if (this.instanceNameProp) {
      this.instanceName = this.instanceNameProp;
    }

    // Note: We no longer connect on mount. Connection happens when open() is called.
    // This ensures we always have a proper UUID before connecting.

    // Standardized event names
    EventBus.$on('ChatAgent.open', this.open);
    EventBus.$on('ChatAgent.close', this.close);
    EventBus.$on('ChatAgent.reset', this.resetInstance);
    EventBus.$on('ChatAgent.updateContext', this.updateContext);

    // Legacy event names (deprecated, kept for backwards compatibility)
    EventBus.$on('openChatBot', this.open);
    EventBus.$on('openChatAgent', this.open);

    EventBus.$on('closeChatBot', () => {
      if (this.isVisible) {
        this.close();
      }
    });
    EventBus.$on('closeChatAgent', () => {
      if (this.isVisible) {
        this.close();
      }
    });

    this.syncViewportSize();
    this.handleViewportResizeBound = this.handleViewportResize.bind(this);
    window.addEventListener('resize', this.handleViewportResizeBound);
  },
  mounted() {
    if (lackey.enabled) {
      lackey.channel('chat-agent', () => this);
    }
  },
  beforeDestroy() {
    if (lackey.enabled) {
      lackey.unregister('chat-agent');
    }

    this.applySidebarLayoutClass(false);

    // Standardized events
    EventBus.$off('ChatAgent.open', this.open);
    EventBus.$off('ChatAgent.close', this.close);
    EventBus.$off('ChatAgent.reset', this.resetInstance);
    EventBus.$off('ChatAgent.updateContext', this.updateContext);

    // Legacy events
    EventBus.$off('openChatBot', this.open);
    EventBus.$off('openChatAgent', this.open);
    EventBus.$off('closeChatBot');
    EventBus.$off('closeChatAgent');
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId);
    }
    window.removeEventListener('resize', this.handleViewportResizeBound);
  },
};
</script>

<style lang="less">
.chat-agent {
  position: fixed;
  max-height: calc(100vh - 40px) !important;
  background: var(--v-lightGray-base);
  border-radius: @base-radius;
  box-shadow: @base-shadow4;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 2147483600;

  // During animation, hide overflow to prevent content flash
  &--animating {
    overflow: hidden;

    .chat-agent__body {
      overflow: hidden;
    }
  }

  // Collapsed mode specific styles
  &--collapsed {
    .chat-agent__header {
      cursor: pointer;

      &:hover {
        filter: brightness(1.1);
      }
    }
  }

  // Sidebar: in-flow panel next to .app-layout
  &--sidebar {
    position: static;
    flex-shrink: 0;
    height: 100%;
    max-height: 100% !important;
    border-radius: 0;
    border-left: 1px solid var(--v-darkGray-base);
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.08);
    z-index: 5;
  }

  // Mobile: Full screen
  &--mobile {
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    border-radius: 0;
    box-shadow: none;
  }

  &__header {
    height: 58px;
    padding: 11px 8px 11px 16px;
    background: var(--gradient);
    font-family: var(--v-headers-font-base);
    font-weight: 600;
    display: flex;
    align-items: center;
    color: #fff;
    font-size: 20px;
    box-shadow: @base-shadow2;
    z-index: 1;
    flex-shrink: 0;
    transition: filter 0.15s ease;

    svg {
      fill: #fff;
    }

    .v-btn {
      transition: transform 0.15s ease, opacity 0.15s ease;

      &:hover {
        transform: scale(1.1);
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &__body {
    flex: 1;
    background: var(--v-lightGray-base);
    padding: 16px;
    overflow: auto;
  }

  &__form {
    display: flex;
    flex-direction: column;
    border-top: 1px solid #ccc;
    position: relative;
    background: #fff;
    box-shadow: 0px -4px 8px rgba(35, 55, 79, 0.12) !important;
    flex-shrink: 0;

    .v-input__slot {
      margin-bottom: 0 !important;
      padding-right: 30px !important;
    }

    &-send {
      position: absolute !important;
      bottom: 6px;
      right: 6px;
    }
  }

  &__staged {
    padding: 8px 12px 4px;

    &-pill {
      display: inline-flex;
      align-items: center;
      max-width: 100%;
      padding: 4px 4px 4px 10px;
      background: #edf2f7;
      border-radius: 16px;
      font-size: 12px;
      line-height: 1.3;
      border: 1px solid #e2e8f0;
      animation: staged-fade-in 0.2s ease;
    }

    &-label {
      font-weight: 600;
      color: #64748b;
      white-space: nowrap;
      margin-right: 4px;
    }

    &-preview {
      color: #94a3b8;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    &-remove {
      flex-shrink: 0;
      margin-left: 2px;
      opacity: 0.5;
      transition: opacity 0.15s ease;

      &:hover {
        opacity: 1;
      }
    }
  }

  @keyframes staged-fade-in {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &__progress {
    position: absolute;
    top: -3px;
    left: 0;
    right: 0;
    width: 100%;
  }

  &__collapsed-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.3);
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    animation: indicator-fade-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes indicator-fade-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &__message {
    padding: 12px;
    margin-bottom: 16px;
    border-radius: @base-radius;
    background-color: var(--v-itemBackground-base);
    box-shadow: @base-shadow;

    &--bot {
      margin-right: 48px;
      background: #fff;
    }

    &--user {
      margin-left: 48px;
      background: #68cdff59;
    }

    &--error {
      border-left: 3px solid var(--v-error-base);
    }

    &-content {
      line-height: 1.6;
      font-size: 15px;

      a {
        color: var(--v-primary-base);
        text-decoration: underline;
        display: inline-block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: bottom;
      }

      .streaming-markdown__content {
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          font-weight: 700;
        }

        h1 {
          font-size: 1.5em;
        }

        h2 {
          font-size: 1.25em;
        }

        h3 {
          font-size: 1.1em;
          font-weight: 600;
        }

        h4 {
          font-size: 1em;
          font-weight: 600;
        }

        h5,
        h6 {
          font-size: 0.875em;
          font-weight: 600;
        }
      }
    }

    &-feedback {
      display: flex;
      gap: 4px;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid rgba(0, 0, 0, 0.06);

      .v-btn {
        opacity: 0.6;
        transition: opacity 0.15s ease, transform 0.15s ease;

        &:hover {
          opacity: 1;
          transform: scale(1.1);
        }

        &.success--text,
        &.error--text {
          opacity: 1;
        }
      }
    }
  }

  &__error {
    display: flex;
    align-items: flex-start;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: rgba(var(--v-error-base-rgb, 244, 67, 54), 0.08);
    border-radius: @base-radius;
    color: var(--v-error-base);
    font-size: 14px;
    line-height: 1.4;

    .v-icon {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  &__tools {
    margin-bottom: 8px;
    font-size: 13px;

    &-header {
      display: flex;
      align-items: center;
      padding: 6px 8px;
      background: rgba(var(--v-appMain-base-rgb, 104, 205, 255), 0.1);
      border-radius: @base-radius;
      cursor: pointer;
      user-select: none;
      color: var(--v-appMain-darken1);
      font-weight: 500;
      transition: background 0.15s ease;

      &:hover {
        background: rgba(var(--v-appMain-base-rgb, 104, 205, 255), 0.18);
      }
    }

    &-spinner {
      animation: spin 1s linear infinite;
    }

    &-details {
      padding: 8px 12px 4px 32px;
    }

    &-item {
      padding: 6px 0;
      color: #666;
      font-size: 12px;

      &-header {
        display: flex;
        align-items: center;
        font-weight: 500;
      }

      .v-icon {
        color: var(--v-appMain-base);
      }
    }

    &-args {
      list-style: none;
      margin: 4px 0 0 18px;
      padding: 0;
    }

    &-arg {
      display: flex;
      align-items: flex-start;
      padding: 2px 0;
      font-size: 11px;
      line-height: 1.3;

      &::before {
        content: '–';
        margin-right: 6px;
        color: #999;
      }

      &--nested {
        padding-left: 16px;

        &::before {
          content: '·';
          color: #bbb;
        }
      }

      &-key {
        color: #888;
        margin-right: 4px;
        flex-shrink: 0;
      }

      &-value {
        color: #555;
        word-break: break-word;

        &--nested {
          color: #777;
          font-style: italic;
        }
      }
    }

    &-nested-count {
      font-size: 10px;
      color: #999;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  &__message-feedback {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    opacity: 0.5;
    transition: opacity 0.15s ease;

    .v-btn {
      transition: transform 0.1s ease;

      &:hover {
        transform: scale(1.15);
      }
    }
  }

  &__message:hover &__message-feedback {
    opacity: 1;
  }

  .v-textarea textarea {
    font-size: 16px !important;
    line-height: 1.4;
  }
}
</style>
